'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FormInput } from '@/components/forms/form-input';
import { ColorPickerPopover } from '@/components/color-picker-popover';
import { createRoleSchema, ROLE_COLORS, type RoleFormValues } from '../schemas';
import {
  useCreateRoleMutation,
  useUpdateRoleMutation
} from '../services/roles.mutations';
import { permissionQueries } from '../services/roles.queries';
import {
  getPermissionAction,
  isColumnPermission,
  isHiddenPermission
} from './role-permissions-matrix-config';
import { useHiddenPermissionGroups } from './use-hidden-permission-groups';
import type { Role, CreateRoleRequest, UpdateRoleRequest } from '../types';

function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** What a brand-new role starts out holding. */
type StartingPoint = 'none' | 'readOnly' | 'full';

/**
 * Role identity: name, colour, and — for a new role — where its permissions
 * start. Permissions themselves are edited in RolePermissionsPanel, which is
 * the single place they live. This dialog used to carry a 16-column
 * permission matrix as well, which meant the same permissions could be
 * edited in two places with two different save semantics.
 */
interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: Role | null;
  /** Called with the new role's id after a successful create. */
  onCreated?: (roleId: number) => void;
}

export function RoleFormDialog({
  open,
  onOpenChange,
  role,
  onCreated
}: RoleFormDialogProps) {
  const t = useTranslations('roles');
  const tv = useTranslations('validation');
  const isEditing = !!role;

  const schema = useMemo(() => createRoleSchema(tv), [tv]);

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name_en: '',
      name_ar: '',
      color: ROLE_COLORS[0]
    }
  });

  const [startingPoint, setStartingPoint] = useState<StartingPoint>('none');
  const selectedColor = useWatch({ control: form.control, name: 'color' });

  const hiddenGroups = useHiddenPermissionGroups();
  const { data: permissionsData } = useQuery({
    ...permissionQueries.list(),
    enabled: open && !isEditing
  });

  /**
   * Action permissions only. The view/column half is left empty on a new
   * role: granting hundreds of column permissions from a radio button
   * nobody read would be a surprise, not a shortcut.
   */
  const actionPermissionNames = useMemo(
    () =>
      (permissionsData?.data ?? [])
        .filter(
          (p) =>
            !isColumnPermission(p.name) &&
            !p.name.endsWith('.view_filter') &&
            !isHiddenPermission(p.name, hiddenGroups)
        )
        .map((p) => p.name),
    [permissionsData, hiddenGroups]
  );

  const resetDialog = useCallback(() => {
    form.reset({
      name_en: role?.name_en ?? '',
      name_ar: role?.name_ar ?? '',
      color: role?.color ?? ROLE_COLORS[0]
    });
    setStartingPoint('none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    if (open) resetDialog();
  }, [open, resetDialog]);

  const createMutation = useCreateRoleMutation();
  const updateMutation = useUpdateRoleMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  function startingPermissions(): string[] {
    if (startingPoint === 'full') return actionPermissionNames;
    if (startingPoint === 'readOnly') {
      return actionPermissionNames.filter(
        (n) => getPermissionAction(n) === 'view'
      );
    }
    return [];
  }

  async function onSubmit(values: RoleFormValues) {
    if (isEditing && role) {
      // Permissions are deliberately echoed back unchanged — this dialog
      // no longer edits them, and omitting them would clear the role.
      await updateMutation.mutateAsync({
        id: role.id,
        data: {
          ...values,
          name: role.name,
          permissions: role.permissions
        } as UpdateRoleRequest
      });
      onOpenChange(false);
      return;
    }

    const created = await createMutation.mutateAsync({
      ...values,
      name: slugify(values.name_en),
      permissions: startingPermissions()
    } as CreateRoleRequest);

    onOpenChange(false);
    if (created?.data?.id) onCreated?.(created.data.id);
  }

  const startingOptions: { value: StartingPoint; label: string }[] = [
    { value: 'none', label: t('startFromNoAccess') },
    { value: 'readOnly', label: t('startFromReadOnly') },
    { value: 'full', label: t('startFromFullAccess') }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editRole') : t('createRole')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('editRoleDescription') : t('createRoleDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-5'
        >
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <FormInput
              control={form.control}
              name='name_en'
              label={t('roleNameEn')}
              placeholder={t('roleNameEnPlaceholder')}
              required
            />
            <FormInput
              control={form.control}
              name='name_ar'
              label={t('roleNameAr')}
              placeholder={t('roleNameArPlaceholder')}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label>{t('roleColor')}</Label>
            {/* The picker's own popover carries the swatch row, so the
                dialog does not repeat it outside the trigger. */}
            <ColorPickerPopover
              value={selectedColor ?? ROLE_COLORS[0]}
              onChange={(hex) =>
                form.setValue('color', hex, { shouldValidate: true })
              }
              label={selectedColor?.toUpperCase()}
              className='w-44'
              presets={ROLE_COLORS}
            />
          </div>

          {!isEditing && (
            <div className='space-y-2'>
              <Label>{t('startFrom')}</Label>
              <RadioGroup
                value={startingPoint}
                onValueChange={(v) => setStartingPoint(v as StartingPoint)}
                className='gap-1.5'
              >
                {startingOptions.map(({ value, label }) => (
                  <div key={value} className='flex items-center gap-2'>
                    <RadioGroupItem value={value} id={`start-${value}`} />
                    <Label
                      htmlFor={`start-${value}`}
                      className='cursor-pointer text-sm font-normal'
                    >
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p className='text-muted-foreground text-xs'>
                {t('startFromHint')}
              </p>
            </div>
          )}
        </Form>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            type='submit'
            disabled={isPending}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isPending ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : (
              <Check className='me-2 h-4 w-4' />
            )}
            {isEditing ? t('saveChanges') : t('createAndEditPermissions')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
